import { i18n } from '@src/i18n-setup';
import { isNil } from 'lodash';
import getMontlyRepayment from '@utils/get-monthly-repayment';
import getOfferUpdatesData from '@utils/offer/get-offer-updates-data';
import getOfferDetailsData from '@utils/offer/get-offer-details-data';
import OfferStateEnum, { getOfferState, isActive, isExpired, isAccepted, isInactive, isCancelled, isAdvanced } from '@enums/offer-state-enum';
import { providerTypeEnum } from '@enums/user-enum';
import RateTypeEnum from '@enums/rate-type-enum';
import OfferTypeEnum from '@enums/offer-type-enum';
import getState from '@utils/get-state';
import getAvatar from '@utils/user/get-avatar';
import { getInitialStates } from '@enums/state-enum';
import getField, { FieldsEnum, getFieldString, getFieldCurrency, getFieldPercent, getFieldCategory } from '@utils/fields/common';

const DEFAULT_LOCALE = 'en-AU';

/**
 * Handy object to be passed to the ld-offer component.
 *
 * @param {Object} enquiry - The original enquiry
 * @param {Object} - The specific offer
 * @param {Integer} - order
 * @return {Object} The processed object to use
 */
export default function getOfferData(enquiry, offer) {
  if (isNil(enquiry) || isNil(offer)) {
    return null;
  }

  const enquiryState = getState(enquiry);
  const isDeclared = isAccepted(offer) && getInitialStates().includes(enquiryState);
  const hasCashback = !isNil(offer.cash_back)
    && offer.cash_back !== 0
    && offer.cash_back !== '0'
    && offer.cash_back !== '0.0';

  return {
    offer: {
      uuid: offer.uuid,
      lender: {
        name: formatOrganisation(offer.organisation),
        logo: offer.organisation.logo_url,
        category: getFieldCategory(offer.organisation),
      },
      broker: getBroker(offer),
      features: [
        {
          label: i18n.t('dashboard.offer.rate_type'),
          value: rateType(offer),
          svg: hasCashback ? 'cash-back' : undefined,
        },
        {
          label: i18n.t('dashboard.offer.interest_rate'),
          value: getFieldPercent(offer.interest_rate),
          svg: offer.discretionary_rate ? 'special' : undefined,
        },
        {
          label: i18n.t('dashboard.offer.comparision_rate'),
          value: getFieldPercent(offer.comparison_rate),
        },
        {
          label: i18n.t('dashboard.offer.details.estimated_repayment'),
          value: getFieldCurrency(getMontlyRepayment(enquiry, offer)),
          tooltip: {
            text: i18n.t('dashboard.offer.details.estimated_repayment_tooltip', {
              loan_amount: getFieldCurrency(enquiry.target_resources[0].data.value),
              repayment_type: getFieldString(`dashboard.offer.details.repayment_type_option.${offer.repayment_type}`, offer.repayment_type),
              years: enquiry.target_resources[0].data.preferences.loan_term_in_years || 30,
            }),
          },
        },
      ],
      extras: [
        { label: i18n.t('dashboard.offer.offset'), value: offer.offset },
        { label: i18n.t('dashboard.offer.split_loan_facility'), value: offer.split_loan_facility },
        { label: i18n.t('dashboard.offer.redraw'), value: offer.redraw },
        { label: i18n.t('dashboard.offer.extra_payments'), value: offer.extra_repayments },
      ],
      details: getOfferDetailsData(offer, enquiry),
      groupedUpdates: getOfferUpdatesData(offer),
      isAutomatic: offer.generated === OfferTypeEnum.AUTOMATIC,
    },
    isExpanded: offer.isExpanded,
    isLeading: offer.ranking === 0,
    isDeclared,
    flag: getFlag(offer),
    stage: getStage(offer, isDeclared),
    _enquiry: enquiry,
    _offer: offer,
  };
}

/**
* Gets the flag label
 *
 * @param {Object} offer
 * @returns {String}
 */
function getFlag(offer) {
  const order = offer.ranking;
  if (isCancelled(offer)) {
    return i18n.t('dashboard.label.flag.cancelled');
  }

  if (isAdvanced(offer) || isInactive(offer)) {
    return i18n.t('dashboard.label.flag.declared');
  }

  if (isExpired(offer)) {
    return i18n.t('dashboard.offer.expired');
  }

  if (order === 0) {
    return i18n.t('dashboard.label.flag.leading');
  }

  return getOrdinalNumber(order + 1);
}

export function getOrdinalNumber(number) {
  const pluralRules = new Intl.PluralRules(DEFAULT_LOCALE, { type: 'ordinal' });
  const suffixes = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th',
  };

  return `${number}${suffixes[pluralRules.select(number)]}`;
}

/**
* Gets the flag label
 *
 * @param {Object} offer
 * @returns {String}
 */
function getBroker(offer) {
  if (offer.fsp.provider_type === providerTypeEnum.BROKER) {
    return {
      name: `${offer.fsp.user.first_name} ${offer.fsp.user.last_name}`,
      avatar: getAvatar(offer.fsp.user),
      company: getField(offer.fsp.organisation.name),
    };
  }

  return null;
}

/**
 * Gets the stage labels, if it applies
 *
 * @param {Object} The offer.
 * @returns {Object}
 */
function getStage(offer, isDeclared) {
  if (isActive(offer) || isExpired(offer)) {
    return null;
  }

  const state = getOfferState(offer.status);

  if (state === OfferStateEnum.CONTACT && isDeclared) {
    return {
      key: 'accepted',
      value: i18n.t(`common.offer.state.lowercase.${state}`),
    };
  }

  if (isNil(state) || state === OfferStateEnum.EXPIRED) {
    return null;
  }

  return {
    key: state,
    value: i18n.t(`common.offer.state.lowercase.${state}`),
  };
}

/**
 * Gets the rate type value for the offer. May be fixed with years or variable.
 * @private
 * @param {Object} The offer.
 * @return {String} The translated value to display.
 */
function rateType(offer) {
  if (offer.rate_type === RateTypeEnum.FIXED) {
    return i18n.t('dashboard.offer.rate_type_period', { years: offer.fixed_period });
  }

  return i18n.t('dashboard.offer.rate_type_variable');
}

const MAX_ORGANISATION_NAME_SIZE = 35;

/**
 * Returns a text representation of the organisation that is not too big.
 * @param {Object} The organisation
 * @return {String} The organisation name.
 */
function formatOrganisation(organisation) {
  if (isNil(organisation) || isNil(organisation.name)) {
    return FieldsEnum.EMPTY;
  }

  if (organisation.name.length < MAX_ORGANISATION_NAME_SIZE) {
    return organisation.name;
  }

  const smallName = organisation.name.substring(0, MAX_ORGANISATION_NAME_SIZE);

  return `${smallName} (...)`;
}
